import { memo, useMemo } from 'react';
import { Skeleton, Stack } from '@mui/material';
import { SortingRule } from 'react-table';
import Table, { ColumnOption } from 'domains/core/components/Table';
import { AccessCentreRole } from 'domains/accessCentre/types';
import { formatTimestamp } from 'domains/accessCentre/util';
import TruncatedTextWithTooltip from 'domains/core/components/TruncatedTextWithTooltip';
import { SortDirection } from 'models/enums';
import RolesTableOptionsCell from './RolesTableOptionsCell';

type RolesTableRow = AccessCentreRole & { lastModifiedSort: Date };

const defaultSortingRules: SortingRule<object>[] = [{ id: 'last_modified_timestamp', desc: true }];

const noopOnSort = (_columnId: string, _direction: SortDirection) => {};

type Props = {
    roles: AccessCentreRole[];
    isLoading?: boolean;
    isRefetching?: boolean;
    refetch?: () => void;
    /** View / Edit / Duplicate in row menu (access_management.manage). Default true. */
    showRoleRowActions?: boolean;
    onSort?: (sortColumn: string, direction: SortDirection) => void;
};

const RolesTable = ({
    roles,
    isLoading,
    isRefetching,
    refetch,
    showRoleRowActions = true,
    onSort = noopOnSort,
}: Props) => {
    const data: object[] = useMemo(
        () =>
            roles.map((r) => ({
                ...r,
                lastModifiedSort: new Date(r.last_modified_timestamp ?? 0),
            })),
        [roles]
    );

    const columns: ColumnOption[] = useMemo(
        () => [
            {
                id: 'name',
                Header: 'Name',
                accessor: 'name',
                align: 'left',
                sortType: 'alphanumeric',
                Cell: (row: any) => <TruncatedTextWithTooltip value={row.cell.value ?? ''} />,
            },
            {
                id: 'user_count',
                Header: 'Number of users',
                accessor: 'user_count',
                align: 'left',
                sortType: 'number',
            },
            {
                id: 'last_modified_timestamp',
                Header: 'Updated at',
                accessor: 'lastModifiedSort',
                sortType: 'datetime',
                Cell: (row: any) => formatTimestamp((row.row.original as RolesTableRow).last_modified_timestamp),
            },
            {
                id: 'updated_by',
                Header: 'Updated by',
                accessor: 'updated_by',
                align: 'left',
                sortType: 'alphanumeric',
                Cell: (row: any) => <TruncatedTextWithTooltip value={row.cell.value ?? ''} />,
            },
            {
                Header: '',
                accessor: 'id',
                align: 'right',
                disableSortBy: true,
                Cell: (val: any) => (
                    <RolesTableOptionsCell
                        role={val.row.original as AccessCentreRole}
                        showActions={showRoleRowActions}
                    />
                ),
            },
        ],
        [showRoleRowActions]
    );

    if (isLoading) {
        return (
            <Stack
                spacing={1}
                sx={{ pt: 2 }}
                data-testid="access-center-roles-table-loading"
                role="progressbar"
                aria-label="Loading roles"
            >
                {[1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rectangular" height={48} />
                ))}
            </Stack>
        );
    }

    return (
        <Stack data-testid="access-center-roles-table" role="table" aria-label="Roles table">
            <Table
                columns={columns}
                data={data}
                defaultSortingRules={defaultSortingRules}
                isRefetching={isRefetching}
                manualSortBy
                onSort={onSort}
                refetch={refetch}
                stickyHeader
            />
        </Stack>
    );
};

export default memo(RolesTable);
